import {
  Box,
  Button,
  Circle,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import { continueSession, initializeChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import MessageComponent from "./MessageComponent";
import { AgentInputProto, AgentScenarioMapping, ArtifactProto, ChatClientType, chatClientTypeToJSON, ChatContext, ChatMessageProto, ChatResponse, ResponseMessageProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ChatMessageType, chatMessageTypeFromJSON, DarwinAgentType, DarwinHeader, ExecutionStatusType } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { AvatarWithMemo, rotateAnimation } from "components/navbar/Navbar";
import logo from "../../assets/logo_dark_small.png";
import { BsRobot } from "react-icons/bs";
import { FiDatabase, FiPlus, FiPlay } from "react-icons/fi";
import { IoAnalytics } from "react-icons/io5";
import { LuSparkles } from "react-icons/lu";
import { MdSchedule } from "react-icons/md";
import { generateRandomSuffix, getRandomWelcomeMessage } from "utils/nameUtils";
import { getAnalyst } from "services/AnalystService";
import showToast from "hooks/useCustomToast";
import { ChatRequest } from "build-ts/chat_session";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useAnalystChat } from "context/AnalystChatContext";
import { extractScenarioJobsSavedByTools } from "utils/helperFuncUtils";
import PromptButton from "./PromptButton";
import ChatAddMenuComponent from "./ChatAddMenuComponent";
import HighlightedTextarea, { type HighlightedTextareaHandle } from "./HighlightedTextarea";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";

interface ChatProps {
  userId?: string;
  isLoadingChat: boolean;
  chatId: string | undefined;
  setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId?: string;
  fetchChatSessionsList?: (clientType: ChatClientType, searchQuery?: string, analyst?: Header | null) => Promise<void>;
  fetchEvaChatSessionsList?: (searchQuery?: string) => Promise<void>;
  // Artifact click handler
  onArtifactClick?: (artifact: ArtifactProto) => void;
  selectedAnalyst?: Header | null;
  // clean content function
  cleanContent?: (content: string) => string;
  // Analyst selection props
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  // Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  // All artifacts from all messages for dataframe link matching
  allArtifacts?: ArtifactProto[];
  setAllArtifacts?: React.Dispatch<React.SetStateAction<ArtifactProto[]>>;
  // Chat is saved if session header has created_date
  isChatSaved?: boolean;
  // Selected scenario ids for the menu: empty/undefined = all scenarios, otherwise multi-scenario context
  selectedScenarioIds?: string[];
  /** When set (e.g. from scenario search bar), initializes session and sends this as first message */
  initialPrompt?: string;
  /** Scenario jobs for the chat */
  scenarioJobs?: ScheduledJobProto[];
  setScenarioJobs?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
  /** Saved scenario agent mapping for the chat */
  savedScenarioAgentMapping?: AgentScenarioMapping[] | undefined;
  /** Set the saved scenario agent mapping for the chat */
  setSavedScenarioAgentMapping?: React.Dispatch<React.SetStateAction<AgentScenarioMapping[] | undefined>>;
  /** Set the scenario jobs saved by tools for the chat */
  setScenarioJobsSavedByTools?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
};

export interface ChatMessage {
  author: "user" | "ai";
  content: string;
  artifacts?: ArtifactProto[] | undefined;
  responseMessage?: ResponseMessageProto | undefined;
  selectedAnalystId?: string;
  ls_trace_url?: string;
  langgraph_message_json_list?: string[];
  userTimestampMs?: number;
  executionStatus?: ExecutionStatusType;
}

const Chat: React.FC<ChatProps> = ({
  userId = "",
  isLoadingChat,
  chatId,
  setChatId,
  chatMessages,
  setChatMessages,
  sessionId,
  fetchChatSessionsList,
  fetchEvaChatSessionsList,
  onArtifactClick,
  selectedAnalyst,
  isFromEva = false,
  cleanContent,
  // Analyst selection props
  analystsList = [],
  isLoadingAnalysts = false,
  onAnalystChange,
  isArtifactsPanelVisible = false,
  allArtifacts = [],
  setAllArtifacts,
  isChatSaved,
  selectedScenarioIds = [],
  initialPrompt,
  scenarioJobs,
  setScenarioJobs,
  savedScenarioAgentMapping,
  setSavedScenarioAgentMapping,
  setScenarioJobsSavedByTools,
}) => {
  const { currentSessionHeader, setCurrentSessionHeader } = useAnalystChat();
  const isInputDisabledDueToEva =
    isFromEva &&
    !!currentSessionHeader &&
    currentSessionHeader.type !== chatClientTypeToJSON(ChatClientType.EVA);
  const [isLoading, setIsLoading] = useState(false);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [welcomeMessage] = useState(() => getRandomWelcomeMessage()); // Generate welcome message on mount
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const lastSentInitialPromptRef = useRef<string | null>(null);
  const highlightedTextareaRef = useRef<HighlightedTextareaHandle>(null);
  const navigate = useNavigate();

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Check if user is near bottom (within 100px)
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 50;

      // Only set userScrolledRef if user actively scrolled away from bottom
      if (!isNearBottom) {
        userScrolledRef.current = true;
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Auto-scroll only when new messages added AND user hasn't manually scrolled away
  useLayoutEffect(() => {
    if (!isLoadingChat && chatMessages.length > 0) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat]);

  // When initialPrompt is set (e.g. from scenario search bar), initialize session and send it once analyst is loaded
  useEffect(() => {
    if (
      !initialPrompt?.trim() ||
      chatId !== undefined ||
      isLoading ||
      lastSentInitialPromptRef.current === initialPrompt
    ) {
      return;
    }
    lastSentInitialPromptRef.current = initialPrompt;
    handleSendMessage(true, null, initialPrompt.trim());
    // Intentionally omit handleSendMessage from deps: we only want to run when analyst/prompt/chatId/isLoading change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, chatId, isLoading]);

  const handleInputChange = (value: string) => {
    setInputMessage(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (chatId || sessionId) {
      // Continue existing chat
      await handleSendMessage();
    } else {
      // New chat
      await handleSendMessage(true, selectedAnalyst);
    }
  };

  const handleSendMessage = async (newChat: boolean = false, selectedAnalyst?: Header | null, messageOverride?: string) => {
    const messageToSend = (messageOverride !== undefined && messageOverride !== null ? messageOverride : inputMessage).trim();
    if (messageToSend !== "" && (chatId || newChat)) {
      const inputMsg = messageToSend;
      userScrolledRef.current = false;
      const userMsg = { content: inputMsg, author: "user" as const };
      const loadingMsg = { content: "", author: "ai" as const };

      // Add user message and loading AI message
      setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      if (messageOverride === undefined) setInputMessage("");
      setIsLoading(true);

      try {
        let response: ChatResponse | undefined;
        if (isFromEva && (chatId || sessionId)) {
          // EVA playground: prefer chatId (updated after duplication) over sessionId (URL param,
          // which doesn't update when window.history.replaceState is used).
          response = await continueSession((chatId ?? sessionId)!, inputMsg, isChatSaved, userId);
          if (response?.saved_scenario_agent_mapping) {
            setSavedScenarioAgentMapping?.(response.saved_scenario_agent_mapping);
          }
        } else if (newChat) {
          // New chat
          if (!selectedAnalyst) {
            showToast("Please select an analyst first", "", "warning");
            return;
          }
          const analystDetails = await getAnalyst(selectedAnalyst!.id!);
          if (!analystDetails) {
            showToast("Failed to load analyst details", "", "error");
            return;
          }

          const scenarioJobs: ScheduledJobProto[] = [];
          if (analystDetails?.header?.id) {
            const jobs = await listScheduledJobsByOwnerId(analystDetails.header.id);
            if (jobs && jobs.length > 0) {
              scenarioJobs.push(...jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
            }
          }
          if (scenarioJobs.length > 0) {
            setScenarioJobs?.(scenarioJobs);
          }
          // Create chat context (analyst chat or scenario creation)
          const envAgentType = process.env.REACT_APP_AGENT_TYPE_FOR_CHAT;
          const agentTypeFromEnv: DarwinAgentType =
            envAgentType !== undefined && envAgentType !== null && envAgentType !== ""
              ? (Number(envAgentType) as DarwinAgentType)
              : DarwinAgentType.ANALYST_CHAT_AGENT;
          const chatContext = ChatContext.create({
            agent_type: agentTypeFromEnv,
            agent_input: AgentInputProto.create({
              analyst: analystDetails,
              scenario_jobs: scenarioJobs
            })
          });

          // Create chat request
          const chatRequest: ChatRequest = ChatRequest.create({
            session_header: DarwinHeader.create({
              id: "",
              name: `Chat ${generateRandomSuffix(8)}`,
              type: chatClientTypeToJSON(ChatClientType.FRIDA),
              author_id: userId,
            }),
            chat_context: chatContext,
            message: inputMsg,
            is_save: true
          });

          // Start both requests at the same instant.
          const chatPromise = initializeChatSession(chatRequest);

          // Shared variables — written by whichever promise resolves first.
          let createdSessionId: string | undefined;
          let generatedTitle: string | undefined;

          const namingDone = initializeChatSession(
            ChatRequest.create({
              session_header: DarwinHeader.create({
                id: "",
                type: chatClientTypeToJSON(ChatClientType.FRIDA),
                author_id: userId,
              }),
              chat_context: ChatContext.create({
                agent_type: DarwinAgentType.CHAT_NAMING_AGENT,
              }),
              message: inputMsg,
              is_save: false,
            }),
          )
            .then((resp) => {
              const titleMsg = resp?.messages?.find(
                (m) => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE && m.content
              );
              generatedTitle = titleMsg?.content?.trim();
              // Naming beat the chat response — show the title immediately using a partial
              // header (id = ""). The full header from the chat response will overwrite this,
              // then namingDone.then re-applies the title once we have the real session ID.
              if (!createdSessionId && generatedTitle) {
                setCurrentSessionHeader(DarwinHeader.create({ name: generatedTitle }));
              }
            })
            .catch((err) => console.warn("Session naming failed (non-critical):", err));

          // Await only the chat response — naming continues independently.
          response = await chatPromise;

          if (response?.saved_scenario_agent_mapping) {
            setSavedScenarioAgentMapping?.(response.saved_scenario_agent_mapping);
          }

          if (response && response.session_header && response.session_header?.id) {
            createdSessionId = response.session_header.id; // assign to outer let
            setChatId(createdSessionId);
            // Set the full header (real id + default name). Title will be patched below.
            setCurrentSessionHeader(response?.session_header);
            navigate(`/chat/${createdSessionId}`, { replace: true });
            // Refresh the session list on chat response.
            if (isFromEva) fetchEvaChatSessionsList?.("");
            else fetchChatSessionsList?.(ChatClientType.FRIDA, "");

            // Once naming is done (may already be), patch header name + persist.
            namingDone.then(() => {
              if (!generatedTitle) return;
              setCurrentSessionHeader((prev) =>
                prev ? { ...prev, name: generatedTitle! } : prev
              );
              updateChatSessionHeaders([createdSessionId!], generatedTitle)
                .catch((err) => console.warn("Failed to apply generated title:", err));
            });
          } else {
            showToast("Failed to create new chat session", "", "error");
          }
        } else {
          // Regular mode: auto-save enabled
          response = await continueSession(chatId!, inputMsg, true, userId);
          if (response?.saved_scenario_agent_mapping) {
            setSavedScenarioAgentMapping?.(response.saved_scenario_agent_mapping);
          }
        }

        // If response has execution status error, show the error UI and stop processing.
        if (response?.status === ExecutionStatusType.ERROR) {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { author: "ai", content: "", executionStatus: ExecutionStatusType.ERROR },
          ]);
          return;
        }

        // Collect new artifacts from response.
        const newArtifacts: ArtifactProto[] = [];

        if (response && response.messages) {
          // Find the index of the last HUMAN message matching the current user input,
          // so we only collect new artifacts from new reasoning messages in the *new* turn.
          const lastHumanMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
            if (
              chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN &&
              msg.content === inputMsg
            ) {
              return idx;
            }
            return lastIdx;
          }, -1);

          const reasoningMessages = response.messages.filter(
            (msg, idx) =>
              idx > lastHumanMsgIdx &&
              chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING &&
              msg.artifacts
          );
          const messagesToProcess =
            reasoningMessages.length > 0
              ? [reasoningMessages[reasoningMessages.length - 1]]
              : reasoningMessages;

          for (const msg of messagesToProcess) {
            for (const artifact of msg.artifacts!) {
              const artifactType = artifactTypeFromJSON(artifact.artifact_type);
              if (
                artifactType === ArtifactType.KG_DATAFRAME ||
                artifactType === ArtifactType.KG_OBSERVATION ||
                artifactType === ArtifactType.SCENARIO_JOB
              ) {
                newArtifacts.push(artifact);
              }
            }
          }
        }

        // Add new artifacts to the cumulative list.
        // For SCENARIO_JOB: replace existing entry with same id; append everything else.
        if (newArtifacts.length > 0 && setAllArtifacts) {
          setAllArtifacts((prev) => {
            let updated = [...prev];
            for (const newArtifact of newArtifacts) {
              if (
                artifactTypeFromJSON(newArtifact.artifact_type) === ArtifactType.SCENARIO_JOB &&
                newArtifact.scenario_job?.header?.id
              ) {
                const jobId = newArtifact.scenario_job.header.id;
                const existingIdx = updated.findIndex(
                  (a) =>
                    artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
                    a.scenario_job?.header?.id === jobId
                );
                if (existingIdx >= 0) {
                  updated[existingIdx] = newArtifact;
                } else {
                  updated.push(newArtifact);
                }
              } else {
                updated.push(newArtifact);
              }
            }
            return updated;
          });
        }

        // Remove the loading message and add the actual AI response
        setChatMessages(prev => {
          const updated = prev.slice(0, -1);

          if (response && response.messages) {
            for (let i = response.messages.length - 1; i >= 0; i--) {
              const msg = response.messages[i] as ChatMessageProto;
              if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.RESPONSE) && msg?.content && msg?.content?.length > 0) {
                updated.push({ author: "ai", content: cleanContent ? cleanContent(msg.content) : msg.content || "", ls_trace_url: msg.ls_trace_url });
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.RESPONSE) && msg?.response_message) {
                updated.push({ author: "ai", content: "", responseMessage: msg.response_message, ls_trace_url: msg.ls_trace_url });
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.REASONING)) {
                // Since we iterate backwards (RESPONSE comes before REASONING in iteration order),
                // attach artifacts to the last AI message we pushed (include SCENARIO so user can click to open scenario)
                const artifactsForMessage = msg.artifacts;
                for (let j = updated.length - 1; j >= 0; j--) {
                  if (updated[j].author === "ai") {
                    updated[j].artifacts = artifactsForMessage && artifactsForMessage.length > 0 ? artifactsForMessage : undefined;
                    updated[j].ls_trace_url = msg.ls_trace_url;
                    updated[j].langgraph_message_json_list = msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0 ? msg.langgraph_message_json_list : [];
                    break;
                  }
                }
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.HUMAN) && msg?.content && msg?.content?.length > 0) {
                if (msg.content === inputMsg) {
                  if (msg.timestamp_ms) {
                    // Find the last user message in updated and stamp it with the response user timestamp
                    for (let k = updated.length - 1; k >= 0; k--) {
                      if (updated[k].author === "user") {
                        updated[k] = { ...updated[k], userTimestampMs: Number(msg.timestamp_ms) };
                        break;
                      }
                    }
                  }
                  break; // break the loop if the user message is the same as the input message
                }
              }
            }
          }
          // Need to set this here because the scenario jobs are saved by tools in the last message of the response
          // and we need to check does it is saved by tools in the last message of the response
          setScenarioJobsSavedByTools?.(extractScenarioJobsSavedByTools([updated[updated.length - 1]]));
          return updated;
        });

        // Refresh sessions list when a new chat is created (naming refresh is handled in Promise.all above)
        if (newChat && response?.session_header?.id) {
          if (isFromEva) {
            fetchEvaChatSessionsList?.("");
          } else {
            fetchChatSessionsList?.(ChatClientType.FRIDA, "");
          }
        }
      } catch (error) {
        // Remove loading message on error
        setChatMessages(prev => prev.slice(0, -1));
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const hasMessages = chatMessages.length > 0;

  // Input area component - reused in both layouts
  const renderInputArea = (width: string) => (
    <Box
      w={width}
      border="1px solid"
      borderColor="gray.300"
      borderRadius="xl"
      bg="white"
      overflow="hidden"
      _focusWithin={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
      boxShadow="sm"
    >
      {!hasMessages ? (
        <>
          <HighlightedTextarea
            id="chat-input"
            ref={highlightedTextareaRef}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="How can I help?"
            minHeight="90px"
            maxHeight="150px"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            fontSize="md"
            px={3}
            pt={3}
            pb={1}
            fontWeight="500"
          />
          <HStack justify="flex-end" spacing={2} m={2}>
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg="purple.50"
                _hover={{ bg: "purple.100" }}
                aria-label="Add attachment"
                flexShrink={0}
              />
              <ChatAddMenuComponent
                analystId={selectedAnalyst?.id ?? ""}
                chatId={chatId ?? ""}
                scenarioJobs={scenarioJobs}
                onSelectAnalysis={(tag) => {
                  setInputMessage(prev => prev ? `${prev} ${tag}` : tag);
                  setTimeout(() => highlightedTextareaRef.current?.focus(), 300);
                }}
              />
            </Menu>
            <IconButton
              icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={5} color="white" />}
              onClick={handleSend}
              bg="purple.600"
              isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
              aria-label="Send Chat Message"
              borderRadius="full"
              size="md"
              _hover={{ bg: "purple.700" }}
              _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            />
          </HStack>
        </>
      ) : (
        // Not empty: Horizontal layout (textarea + button inline)
        <HStack spacing={2} align="flex-end" p={2}>
          <HighlightedTextarea
            ref={highlightedTextareaRef}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Reply..."
            minHeight="40px"
            maxHeight="150px"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            fontSize="md"
            px={2}
            py={2}
            flex="1"
          />
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg="purple.50"
              _hover={{ bg: "purple.100" }}
              aria-label="Add attachment"
              flexShrink={0}
              alignSelf="center"
            />
            <ChatAddMenuComponent
              analystId={selectedAnalyst?.id ?? ""}
              chatId={chatId ?? ""}
              scenarioJobs={scenarioJobs}
              onSelectAnalysis={(tag) => {
                setInputMessage(prev => prev ? `${prev} ${tag}` : tag);
                setTimeout(() => highlightedTextareaRef.current?.focus(), 300);
              }}
            />
          </Menu>
          <IconButton
            icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={4} color="white" />}
            onClick={handleSend}
            bg="purple.600"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            aria-label="Send Chat Message"
            borderRadius="full"
            size="sm"
            _hover={{ bg: "purple.700" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            flexShrink={0}
            alignSelf="center"
          />
        </HStack>
      )}
    </Box>
  );

  // Loading state
  if (isLoadingChat) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Loading...
        </Text>
      </Box>
    );
  }

  // Empty state - centered layout
  if (!hasMessages) {
    return (
      <Box position="relative" w="100%" h="100%" display="flex" flexDirection="column">
        {/* Analyst dropdown - top left (FRIDA chat only) */}
        {!isFromEva && (
          <Box position="absolute" top={4} left={4} zIndex={10}>
            {isLoadingAnalysts ? (
              <Spinner size="sm" color="purple.500" />
            ) : (
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  minW="220px"
                  maxW="500px"
                  bg="purple.50"
                  fontSize="sm"
                  fontWeight="600"
                  borderRadius="lg"
                  color="gray.700"
                  _hover={{ bg: "purple.100" }}
                  _active={{ bg: "purple.100" }}
                  rightIcon={<ChevronDownIcon />}
                  textAlign="left"
                  px={3}
                >
                  <HStack spacing={2}>
                    <Icon as={BsRobot} boxSize={6} color="purple.600" />
                    <Text isTruncated fontSize="sm" mb={0} fontWeight="600">
                      {selectedAnalyst?.name || "Select analyst"}
                    </Text>
                  </HStack>
                </MenuButton>
                <Portal>
                  <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
                    {analystsList.length === 0 ? (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No analysts available
                      </MenuItem>
                    ) : (
                      analystsList.map((analyst) => (
                        <MenuItem
                          key={analyst.id}
                          onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                          fontSize="sm"
                          _hover={{ bg: "purple.50" }}
                          _focus={{ bg: "purple.50" }}
                          fontWeight="500"
                        >
                          {analyst.name}
                        </MenuItem>
                      ))
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </Box>
        )}
        <VStack
          flex={1}
          w="100%"
          h="100%"
          justify="center"
          align="center"
          spacing={9}
          mt={-3}
        >
          <HStack spacing={5}>
            <Circle bg="purple.50" p={2}>
              <AvatarWithMemo
                src={logo}
                animation={`${rotateAnimation} 3s linear infinite`}
                name="Anko"
                size="md"
              />
            </Circle>
            <Text color="gray.700" fontSize="3xl" fontWeight="400" m={0}>
              {welcomeMessage}
            </Text>
          </HStack>
          {renderInputArea("700px")}
          {/* Prompt buttons - FRIDA chat only */}
          {!isFromEva && (
            <VStack spacing={3} align="flex-start" maxW="700px" w="100%">
              <HStack
                display={{ base: "none", md: "flex" }}
                flexShrink={0}
                color="purple.600"
                fontSize="sm"
                fontWeight="medium"
              >
                <Icon as={LuSparkles} boxSize={4} />
                <Text m={0}>Try these</Text>
              </HStack>
              <SimpleGrid columns={2} spacing={2} w="100%">
                {[
                  { text: "Explore data", icon: FiDatabase, description: "Query and explore my metrics" },
                  { text: "Start a new analysis", icon: IoAnalytics, description: "Create an analysis from scratch" },
                  { text: "Schedule an automated alert", icon: MdSchedule, description: "Set up recurring alerts" },
                  { text: "Run an existing analysis", icon: FiPlay, description: "Execute a saved analysis" },
                ].map((prompt) => (
                  <PromptButton
                    key={prompt.text}
                    icon={<Icon as={prompt.icon} boxSize={5} />}
                    title={prompt.text}
                    description={prompt.description}
                    onClick={() => {
                      handleSendMessage(!(chatId || sessionId), selectedAnalyst, prompt.description);
                    }}
                    w="100%"
                  />
                ))}
              </SimpleGrid>
            </VStack>
          )}
        </VStack>
      </Box>
    );
  }

  // With messages - bottom input layout
  return (
    <VStack w="100%" h="100%" m={0} spacing={0} mt={1}>
      <Box
        ref={messagesContainerRef}
        flex="1"
        w="100%"
        overflow="auto"
        onScroll={handleScroll}
        p={4}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-gray-200)",
            borderRadius: "full",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "var(--chakra-colors-gray-400)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "var(--chakra-colors-gray-400) transparent",
        }}
      >
        <VStack w={isArtifactsPanelVisible ? "95%" : "80%"} mx="auto" align="stretch" spacing={0}>
          <Box flex="1" minH="0" />
          {chatMessages.map((msg, index) => (
            <MessageComponent
              key={index}
              author={msg.author as "user" | "ai"}
              content={msg.content}
              responseMessage={msg.responseMessage}
              artifacts={msg.artifacts}
              langgraphMessageJsonList={msg.langgraph_message_json_list}
              onArtifactClick={onArtifactClick}
              isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
              isLastMessage={index === chatMessages.length - 1}
              lsTraceUrl={isFromEva ? msg.ls_trace_url : undefined}
              chatMessages={chatMessages}
              isArtifactsPanelVisible={isArtifactsPanelVisible}
              allArtifactsForLinks={allArtifacts}
              scenarioJobs={scenarioJobs}
              userTimestampMs={msg.userTimestampMs}
              executionStatus={msg.executionStatus}
              savedScenarioAgentMapping={savedScenarioAgentMapping}
            />
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>
      <Box w="100%" p={4} bg="bg.surface" display="flex" flexDirection="column" alignItems="center" mb={isArtifactsPanelVisible ? 1 : 1}>
        {renderInputArea(isArtifactsPanelVisible ? "90%" : "80%")}
        <Text fontSize="xs" color="fg.muted" mt={2} mb={0}>
          Anko is AI and may make mistakes. Please double check responses.
        </Text>
      </Box>
    </VStack>
  );
};

export default Chat;